import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { TextInput } from "@gravity-ui/uikit";
import { applyTimeDigitSequence, caretToDigitIndex, digitIndexToCaret, formatTimeDigits, isTimeDigitsComplete, removeDigitAfter, removeDigitBefore, timeToDigits, } from "./timeInputUtils";
export const TimeInput = ({ value, onCommit, disabled, placeholder, size = "m", className, }) => {
    const controlRef = useRef(null);
    const digitsRef = useRef(timeToDigits(value));
    const isFocusedRef = useRef(false);
    const replaceOnTypeRef = useRef(false);
    const pointerFocusRef = useRef(false);
    const skipBlurCommitRef = useRef(false);
    const pendingCaretRef = useRef(null);
    const [digits, setDigits] = useState(() => timeToDigits(value));
    useEffect(() => {
        if (isFocusedRef.current) {
            return;
        }
        const nextDigits = timeToDigits(value);
        digitsRef.current = nextDigits;
        setDigits(nextDigits);
    }, [value]);
    const displayValue = formatTimeDigits(digits);
    useLayoutEffect(() => {
        const input = controlRef.current;
        const caret = pendingCaretRef.current;
        if (!input || caret === null) {
            return;
        }
        pendingCaretRef.current = null;
        input.setSelectionRange(caret, caret);
    }, [digits, displayValue]);
    function setNextDigits(nextDigits, caret) {
        digitsRef.current = nextDigits;
        pendingCaretRef.current =
            caret ?? digitIndexToCaret(nextDigits.length, nextDigits);
        setDigits(nextDigits);
        return nextDigits;
    }
    function commitAndBlur(nextDigits) {
        skipBlurCommitRef.current = true;
        onCommit(formatTimeDigits(nextDigits));
        controlRef.current?.blur();
    }
    function hasFullSelection() {
        const input = controlRef.current;
        if (!input || !input.value) {
            return false;
        }
        return (input.selectionStart === 0 &&
            input.selectionEnd === input.value.length);
    }
    function handleFocus() {
        skipBlurCommitRef.current = false;
        isFocusedRef.current = true;
        pendingCaretRef.current = null;
        const nextDigits = timeToDigits(value);
        if (digitsRef.current !== nextDigits) {
            digitsRef.current = nextDigits;
            setDigits(nextDigits);
        }
        if (pointerFocusRef.current) {
            pointerFocusRef.current = false;
            replaceOnTypeRef.current = false;
            return;
        }
        replaceOnTypeRef.current = true;
        requestAnimationFrame(() => {
            if (replaceOnTypeRef.current) {
                controlRef.current?.select();
            }
        });
    }
    function handleBlur() {
        isFocusedRef.current = false;
        replaceOnTypeRef.current = false;
        pointerFocusRef.current = false;
        pendingCaretRef.current = null;
        if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            return;
        }
        onCommit(formatTimeDigits(digitsRef.current));
    }
    function getReplaceSource(incomingDigits, previousDigits) {
        if (incomingDigits.length === 1) {
            return incomingDigits;
        }
        if (incomingDigits.length === previousDigits.length + 1) {
            if (incomingDigits.startsWith(previousDigits)) {
                return incomingDigits.slice(-1);
            }
            if (incomingDigits.endsWith(previousDigits)) {
                return incomingDigits[0];
            }
        }
        return incomingDigits;
    }
    function handleUpdate(nextValue) {
        const incomingDigits = nextValue.replace(/\D/g, "");
        const previousDigits = digitsRef.current;
        let nextDigits;
        if (replaceOnTypeRef.current || hasFullSelection()) {
            replaceOnTypeRef.current = false;
            nextDigits = applyTimeDigitSequence("", getReplaceSource(incomingDigits, previousDigits));
        }
        else if (incomingDigits.length < previousDigits.length &&
            previousDigits.startsWith(incomingDigits)) {
            nextDigits = incomingDigits.slice(0, 4);
        }
        else if (incomingDigits.startsWith(previousDigits)) {
            nextDigits = applyTimeDigitSequence(previousDigits, incomingDigits.slice(previousDigits.length));
        }
        else {
            nextDigits = applyTimeDigitSequence("", incomingDigits);
        }
        setNextDigits(nextDigits);
        if (isTimeDigitsComplete(nextDigits)) {
            commitAndBlur(nextDigits);
        }
    }
    function handleKeyDown(event) {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            commitAndBlur(digitsRef.current);
            return;
        }
        if (event.key === ":") {
            event.preventDefault();
            return;
        }
        if (event.key !== "Backspace" && event.key !== "Delete") {
            return;
        }
        event.preventDefault();
        if (replaceOnTypeRef.current || hasFullSelection()) {
            replaceOnTypeRef.current = false;
            setNextDigits("");
            return;
        }
        const input = controlRef.current;
        const caret = input?.selectionStart ?? displayValue.length;
        const digitIndex = caretToDigitIndex(caret, digitsRef.current);
        const nextDigits = event.key === "Delete"
            ? removeDigitAfter(digitsRef.current, digitIndex)
            : removeDigitBefore(digitsRef.current, digitIndex);
        if (nextDigits === digitsRef.current) {
            return;
        }
        const nextCaretIndex = event.key === "Delete" ? digitIndex : Math.max(0, digitIndex - 1);
        setNextDigits(nextDigits, digitIndexToCaret(nextCaretIndex, nextDigits));
    }
    function handlePaste(event) {
        event.preventDefault();
        replaceOnTypeRef.current = false;
        const pastedDigits = event.clipboardData
            .getData("text")
            .replace(/\D/g, "");
        const nextDigits = applyTimeDigitSequence("", pastedDigits);
        setNextDigits(nextDigits);
        if (isTimeDigitsComplete(nextDigits)) {
            commitAndBlur(nextDigits);
        }
    }
    function handlePointerDown() {
        pointerFocusRef.current = true;
    }
    return (_jsx(TextInput, { size: size, className: className, placeholder: placeholder, value: displayValue, disabled: disabled, controlRef: controlRef, onUpdate: handleUpdate, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown, controlProps: {
            inputMode: "numeric",
            autoComplete: "off",
            spellCheck: false,
            onPaste: handlePaste,
            onPointerDown: handlePointerDown,
        } }));
};
